import { Button, Select, Space, Table, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import RunDetailModal from "./RunDetailModal";
import {
  DELIVERY_STATUS_ATIVOS,
  DELIVERY_STATUS_LABELS,
  STATUS_ATIVOS,
  STATUS_LABELS,
  StatusTag,
  formatDate,
  formatDateTime,
  formatUsd,
} from "./status";

const { Text } = Typography;

export default function RunsCard({ catalog, routines }) {
  const { getAiReportRuns } = useApi();
  const [filtros, setFiltros] = useState({});
  const [aberto, setAberto] = useState(null);

  const rotinas = Object.fromEntries(
    (routines || []).map((r) => [r.id, r.name])
  );
  const filiais = Object.fromEntries(
    (catalog?.branches || []).map((b) => [
      b.id,
      `${b.name}${b.store_code ? ` (${b.store_code})` : ""}`,
    ])
  );

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["ai-report-runs", filtros],
    queryFn: () => getAiReportRuns({ limit: 100, ...filtros }),
    refetchInterval: (query) =>
      (query.state.data?.runs || []).some(
        (r) =>
          STATUS_ATIVOS.includes(r.status) ||
          DELIVERY_STATUS_ATIVOS.includes(r.entrega_status)
      )
        ? 5000
        : false,
    staleTime: 0,
  });
  const runs = data?.runs || [];

  const colunas = [
    { title: "#", dataIndex: "id", key: "id", width: 70 },
    {
      title: "Rotina",
      dataIndex: "routine_id",
      key: "routine",
      render: (id, run) => rotinas[id] || run.routine_name || id,
    },
    {
      title: "Filial",
      dataIndex: "branch_id",
      key: "branch",
      render: (id) => filiais[id] || id,
    },
    {
      title: "Período",
      key: "periodo",
      render: (_, r) =>
        `${formatDate(r.periodo_inicio)} → ${formatDate(r.periodo_fim)}`,
    },
    { title: "Origem", dataIndex: "origem", key: "origem", width: 100 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (s) => <StatusTag status={s} />,
    },
    {
      title: "Entrega",
      dataIndex: "entrega_status",
      key: "entrega",
      width: 175,
      render: (s) => <StatusTag status={s} mapa={DELIVERY_STATUS_LABELS} />,
    },
    {
      title: "Tokens",
      key: "tokens",
      align: "right",
      render: (_, r) =>
        r.input_tokens != null ? `${r.input_tokens} / ${r.output_tokens}` : "—",
    },
    {
      title: "Custo",
      dataIndex: "custo_usd",
      key: "custo",
      align: "right",
      render: formatUsd,
    },
    {
      title: "Criado",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: formatDateTime,
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => setAberto(r.id)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="Rotina"
          style={{ width: 220 }}
          options={(routines || []).map((r) => ({
            value: r.id,
            label: r.name,
          }))}
          onChange={(v) =>
            setFiltros((f) => ({ ...f, routine_id: v || undefined }))
          }
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Filial"
          style={{ width: 260 }}
          options={Object.entries(filiais).map(([id, label]) => ({
            value: Number(id),
            label,
          }))}
          onChange={(v) =>
            setFiltros((f) => ({ ...f, branch_id: v || undefined }))
          }
        />
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 200 }}
          options={Object.entries(STATUS_LABELS).map(([value, s]) => ({
            value,
            label: s.label,
          }))}
          onChange={(v) =>
            setFiltros((f) => ({ ...f, status: v || undefined }))
          }
        />
        <Button onClick={() => refetch()} loading={isFetching}>
          Atualizar
        </Button>
        {runs.some((r) => STATUS_ATIVOS.includes(r.status)) && (
          <Text type="secondary">
            Há execuções na fila; atualizando a cada 5 s.
          </Text>
        )}
      </Space>
      <Table
        dataSource={runs}
        columns={colunas}
        rowKey="id"
        size="middle"
        loading={isFetching && !data}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "Nenhuma execução." }}
        rowClassName={(r) =>
          r.entrega_suprimida ? "ai-report-run-suprimido" : ""
        }
      />
      <RunDetailModal
        runId={aberto}
        onClose={() => setAberto(null)}
        rotinas={rotinas}
        filiais={filiais}
        podeEnviar={Boolean(
          catalog?.pode_gerenciar && catalog?.entrega_habilitada
        )}
      />
    </div>
  );
}
